import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { noop } from '@lux/helpers';

import { Text, theme } from '@dls/web';
import useTranslate from '../../hooks/useTranslation';

const Dots = styled.div`
  display: flex;
  margin: 1rem auto;
  width: 100%;
`;

const Dot = styled.div`
  height: ${p => p.theme.spacing(5)};
  width: ${p => p.theme.spacing(5)};
  border-radius: 20px;
  margin: ${p => p.theme.spacing(1)};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.32) inset;
  text-align: center;
  cursor: pointer;
  position: relative;
  background-color: ${p => p.hexCode};

  ${p =>
    !p.active &&
    css`
      &:hover {
        &:after {
          content: '';
          position: absolute;
          top: -4px;
          left: -4px;
          right: -4px;
          bottom: -4px;
          border: 1px solid ${p.theme.colours.grey_600};
          border-radius: 50%;
        }
      }
    `};

  &:first-of-type {
    margin-left: 0;
  }

  &:last-of-type {
    margin-right: ${p => p.theme.spacing(0.5)};
  }

  ${p =>
    p.active &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border: 2px solid ${theme.main.colours.secondary.d1};
        border-radius: 50%;
      }
    `};

  ${p =>
    p.isDisabled &&
    p.active &&
    css`
      &:after {
        content: '';
        position: absolute;
        top: -4px;
        left: -4px;
        right: -4px;
        bottom: -4px;
        border: 2px solid ${theme.main.colours.secondary.d1};
        border-radius: 50%;
  `};

  ${p =>
    p.isDisabled &&
    !p.active &&
    css`
      border: 1px solid ${p.theme.colours.grey_500};
    `};
`;

const Slash = styled.div`
  display: none;
  border-bottom: 1px solid ${p => p.theme.colours.grey_500};
  border-radius: 0;
  width: 100%;
  transform: rotate(-45deg);
  height: 50%;
  transform-origin: 50% 100%;
  display: block;
`;

const PhoneColourSelection = ({ selectedColour, colours, onChange }) => {
  const { t } = useTranslate();

  useEffect(() => {
    if (colours.length === 1 || !selectedColour) {
      onChange(colours[0]);
    }
  }, [colours, onChange, selectedColour]);

  return (
    <Fragment>
      <Text type="header">{t('CHOOSE_YOUR_COLOR', [selectedColour])}</Text>
      <Dots>
        {colours.map(colour => {
          const { active, hexCode, available } = colour;
          return (
            <Dot
              key={hexCode}
              data-testid={hexCode}
              hexCode={hexCode}
              active={active}
              onClick={() => onChange(colour)}
              isDisabled={!available}
            >
              {!available && <Slash data-testid={`slash-${hexCode}`} />}
            </Dot>
          );
        })}
      </Dots>
    </Fragment>
  );
};

PhoneColourSelection.defaultProps = {
  selectedColour: '',
  colours: [],
  onChange: noop
};

const colourObjShape = PropTypes.shape({
  name: PropTypes.string,
  hexCode: PropTypes.string,
  active: PropTypes.bool
});

PhoneColourSelection.propTypes = {
  /** Selected colour name */
  selectedColour: PropTypes.string,
  /** List of colours to render */
  colours: PropTypes.arrayOf(colourObjShape),
  /** onChange event handler, returns full colour object */
  onChange: PropTypes.func
};

export default PhoneColourSelection;
